import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Gem, Loader } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'

const DoubleHistory = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loadHistory } = useDoubleStore()

  const [activeTab, setActiveTab] = useState('all')
  const [allRounds, setAllRounds] = useState([])
  const [myRounds, setMyRounds] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const rounds = activeTab === 'all' ? allRounds : myRounds

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        const data = await loadHistory()
        setAllRounds(data.all || [])
        setMyRounds(data.mine || [])
      } catch (error) {
        toast.error(t('double.history.loadError', 'Не удалось загрузить историю'))
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [loadHistory, t])

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="relative flex items-center justify-center lg:min-h-[48px]">
          <button
            onClick={() => navigate('/double')}
            className="absolute left-0 inline-flex items-center justify-center rounded-xl border border-white/10 bg-surface p-2.5 text-white hover:text-accent-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl lg:text-4xl font-bold text-white">{t('double.history.title', 'История Double')}</h1>
        </div>

        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'all' ? 'bg-accent-primary text-dark-primary' : 'bg-surface text-text-secondary'
            }`}
          >
            {t('double.history.tabs.all', 'Все')}
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'my' ? 'bg-accent-primary text-dark-primary' : 'bg-surface text-text-secondary'
            }`}
          >
            {t('double.history.tabs.my', 'Мои')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-white/10 bg-surface/50">
            <Loader className="w-8 h-8 text-accent-primary animate-spin" />
          </div>
        ) : rounds.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-surface/50 p-8 text-center text-text-secondary">
            {t('double.history.empty', 'История пуста')}
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {rounds.map((round) => {
              const totalRoundSum = Number(round.sum_red || 0) + Number(round.sum_black || 0) + Number(round.sum_green || 0)
              const winColorLabel = round.win_color === 'red'
                ? t('double.colors.red', 'Красный')
                : round.win_color === 'black'
                  ? t('double.colors.black', 'Чёрный')
                  : t('double.colors.green', 'Зелёный')

              return (
              <button
                key={round.id}
                onClick={() => navigate(`/double/history/${round.id}`)}
                className="rounded-2xl border border-white/10 bg-surface/50 p-4 text-left hover:border-accent-primary/40"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-white font-semibold">#{String(round.id).slice(0, 10)}...</p>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    round.win_color === 'red'
                      ? 'bg-red-500/15 text-red-300'
                      : round.win_color === 'black'
                        ? 'bg-black/40 text-slate-200'
                        : 'bg-emerald-500/15 text-emerald-300'
                  }`}>
                    {winColorLabel} · {round.win_sector}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-2 text-red-200">
                    R: {Number(round.sum_red || 0).toLocaleString()}
                  </div>
                  <div className="rounded-lg bg-black/35 border border-white/20 p-2 text-slate-200">
                    B: {Number(round.sum_black || 0).toLocaleString()}
                  </div>
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-300/30 p-2 text-emerald-200">
                    G: {Number(round.sum_green || 0).toLocaleString()}
                  </div>
                </div>

                <p className="mt-3 text-sm text-text-secondary inline-flex items-center gap-1">
                  <Gem className="w-4 h-4 text-accent-primary" />
                  {totalRoundSum.toLocaleString()}
                </p>
              </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default DoubleHistory
